import { useEffect, useState } from 'react';
import { Link2, Loader2, Search, Sparkles, Unlink } from 'lucide-react';
import type { GuardianLink, GuardianSuggestion } from 'shared';
import { useParticipants } from '@/hooks/useParticipants';
import { birthAndAge, formatDay, matchedOnLabel } from '@/lib/guardianView';

// The three ways a guardian's educandos appear in the backoffice: linked now,
// suggested, and found by hand. Nothing here links anyone until a member of
// staff presses Associar.

const sectionTitle = 'text-sm font-semibold text-foreground flex items-center gap-2';
const linkButton =
  'flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0';

interface LinkButtonProps {
  name: string;
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
}

function AssociarButton({ name, pending, disabled, onClick }: LinkButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Associar ${name}`}
      className={linkButton}
    >
      {pending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
      Associar
    </button>
  );
}

// ─── Linked now ──────────────────────────────────────────────────────────────

interface EducandosSectionProps {
  links: GuardianLink[];
  onUnlink: (link: GuardianLink) => void;
}

export function EducandosSection({ links, onUnlink }: EducandosSectionProps) {
  return (
    <section aria-labelledby="educandos-title" className="space-y-3">
      <h3 id="educandos-title" className={sectionTitle}>
        Educandos associados
        <span className="px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground tabular-nums">
          {links.length}
        </span>
      </h3>
      {links.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Ainda sem educandos. Até haver uma associação, esta pessoa não vê nenhum participante na app.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {links.map((link) => (
            <li key={link.participantId} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">{link.participantName}</p>
                <p className="text-xs text-muted-foreground">
                  {birthAndAge(link.dataNascimento)}
                  {link.digitalCenterName ? ` · ${link.digitalCenterName}` : ''}
                </p>
                <p className="text-xs text-muted-foreground">
                  Associado a {formatDay(link.linkedAt)} por {link.linkedByName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onUnlink(link)}
                aria-label={`Remover associação de ${link.participantName}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors shrink-0"
              >
                <Unlink className="w-3.5 h-3.5" />
                Remover associação
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Suggested ───────────────────────────────────────────────────────────────

interface SuggestionsSectionProps {
  suggestions: GuardianSuggestion[];
  linkingId: number | null;
  onLink: (participantId: number) => void;
}

export function SuggestionsSection({ suggestions, linkingId, onLink }: SuggestionsSectionProps) {
  return (
    <section aria-labelledby="sugestoes-title" className="space-y-3">
      <div>
        <h3 id="sugestoes-title" className={sectionTitle}>
          <Sparkles className="w-4 h-4 text-primary" />
          Sugestões
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Participantes cujo registo indica este email ou telemóvel como o do Encarregado de
          Educação. Nenhum fica associado sem confirmação.
        </p>
      </div>
      {suggestions.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sem sugestões.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {suggestions.map((s) => (
            <li key={s.participantId} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">{s.participantName}</p>
                <p className="text-xs text-muted-foreground">
                  {birthAndAge(s.dataNascimento)}
                  {s.digitalCenterName ? ` · ${s.digitalCenterName}` : ''}
                </p>
                <p className="text-xs text-primary">{matchedOnLabel(s.matchedOn)}</p>
              </div>
              <AssociarButton
                name={s.participantName}
                pending={linkingId === s.participantId}
                disabled={linkingId !== null}
                onClick={() => onLink(s.participantId)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Found by hand ───────────────────────────────────────────────────────────

interface ParticipantSearchProps {
  linkedIds: ReadonlySet<number>;
  linkingId: number | null;
  onLink: (participantId: number) => void;
}

export function ParticipantLinkSearch({ linkedIds, linkingId, onLink }: ParticipantSearchProps) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(timer);
  }, [term]);

  return (
    <section aria-labelledby="procurar-title" className="space-y-3">
      <h3 id="procurar-title" className={sectionTitle}>
        <Search className="w-4 h-4 text-primary" />
        Procurar participante
      </h3>
      <input
        type="search"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Nome, email ou número…"
        aria-label="Procurar participante para associar"
        className="input w-full"
      />
      {debounced.length >= 2 && (
        <SearchResults search={debounced} linkedIds={linkedIds} linkingId={linkingId} onLink={onLink} />
      )}
    </section>
  );
}

interface SearchResultsProps extends ParticipantSearchProps {
  search: string;
}

function SearchResults({ search, linkedIds, linkingId, onLink }: SearchResultsProps) {
  const { data, isLoading, isError } = useParticipants({ search, limit: 8 });
  const participants = data?.participants ?? [];

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground flex items-center gap-2" role="status">
        <Loader2 className="w-4 h-4 animate-spin" /> A procurar…
      </p>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Não foi possível procurar participantes.
      </p>
    );
  }
  if (participants.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhum participante encontrado.</p>;
  }

  return (
    <ul className="divide-y divide-border rounded-lg border border-border" aria-label="Resultados da pesquisa">
      {participants.map((p) => {
        const name = `${p.nome} ${p.apelido}`;
        const linked = linkedIds.has(p.id);
        return (
          <li key={p.id} className="flex items-center gap-3 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">{name}</p>
              <p className="text-xs text-muted-foreground">
                {birthAndAge(p.dataNascimento)}
                {p.digitalCenterName ? ` · ${p.digitalCenterName}` : ''}
              </p>
            </div>
            {linked ? (
              <span className="text-xs text-muted-foreground shrink-0">Já associado</span>
            ) : (
              <AssociarButton
                name={name}
                pending={linkingId === p.id}
                disabled={linkingId !== null}
                onClick={() => onLink(p.id)}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
